<script setup lang="ts">
import type { PropertyFilters } from '~/core/entities/property/filters';

import { useIntersectionObserver } from '@vueuse/core';
import { useEvents } from '~/composables/events/useEvents';
import { useShortsSrpVariant } from '~/composables/shorts/useShortsSrpVariant';
import { useMixpanel } from '~/composables/useMixpanel';
import { withSrpVariantRecommendationParams } from '~/composables/useShortsFilters/featuredVideosFilterRules';
import { ORDER_BY, SHORTS_SORT_BY } from '~/core/entities/property/filters';
import { MixpanelProps } from '~/core/service/mixpanel';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { dehyphenateString } from '~~/utils';

import SectionSkeleton from '~/components/Sections/ShortsSection/SectionSkeleton.vue';

const props = defineProps<{
	infoLabel?: string | null;
	titleOverride?: string;
	skipPageviewTracking?: boolean;
	fetchPage?: number;
	fetchPerPage?: number;
	maxVideos?: number;
	deferUntilVisible?: boolean;
	visibilityRootMargin?: string;
	/**
	 * Filtros reativos da listagem (`useManager`). Incluem cidade/UF/bairro após a API
	 * aplicar `locationSlugFilters` — necessário porque `getFiltersFromPath` só lê a URL
	 * e não preenche `cities`/`states` para slugs como `sao-paulo-sp-brasil`.
	 */
	sessionFilters?: PropertyFilters;
}>();

const { trackPageView } = useEvents();
const mixpanel = useMixpanel();
const route = useRoute();
const rootRef = ref<HTMLElement | null>(null);
const hasEnteredViewport = ref(!props.deferUntilVisible);

let stopVisibilityObserver: (() => void) | undefined;

let pageviewTracked = false;
onMounted(() => {
	pageviewTracked = false;

	if (!props.deferUntilVisible) {
		hasEnteredViewport.value = true;
		return;
	}

	if (!window.IntersectionObserver) {
		hasEnteredViewport.value = true;
		return;
	}

	const observer = useIntersectionObserver(
		rootRef,
		entries => {
			const entry = entries[0];
			if (!entry?.isIntersecting) return;

			hasEnteredViewport.value = true;
			stopVisibilityObserver?.();
		},
		{ rootMargin: props.visibilityRootMargin ?? '800px 0px' }
	);
	stopVisibilityObserver = observer.stop;
});

onBeforeUnmount(() => {
	stopVisibilityObserver?.();
});

function handleShortsHasVideos(hasVideos: boolean): void {
	if (props.skipPageviewTracking) return;
	if (pageviewTracked) return;
	pageviewTracked = true;
	trackPageView({
		current_page: 'listed properties',
		previous_page_url: document.referrer
	});
	mixpanel.trackPageview({
		[MixpanelProps.HAS_SHORTS_SECTION]: hasVideos
	});
}

const pathFilters = computed(() =>
	PropertyFiltersPresenter.getFiltersFromPath(
		route.fullPath,
		undefined,
		'imoveis'
	)
);

const listingFilters = computed(
	() => props.sessionFilters ?? pathFilters.value
);

const addressSlug = computed(() => {
	const addressSlug = route.query.addressSlug;

	if (Array.isArray(addressSlug)) {
		return addressSlug.find(Boolean) || listingFilters.value.addressSlug || '';
	}

	return addressSlug || listingFilters.value.addressSlug || '';
});

const infoLabelFallback = computed(() => {
	const normalizedInfoLabel = props.infoLabel?.replace(/^em\s+/i, '').trim();

	if (normalizedInfoLabel) {
		return normalizedInfoLabel;
	}

	const locationSlug = listingFilters.value.locationSlug;

	if (!locationSlug) {
		return '';
	}

	const locationParts = locationSlug.split('-');

	if (locationParts.at(-1) !== 'brasil' || locationParts.length < 3) {
		return '';
	}

	const state = locationParts.at(-2)?.toUpperCase();
	const citySlug = locationParts.slice(0, -2).join('-');

	if (!citySlug || !state) {
		return '';
	}

	return `${dehyphenateString(citySlug)} - ${state}`;
});

const titleLocation = computed(() => {
	if (addressSlug.value) {
		const firstLocation = addressSlug.value?.split(',')[0]?.trim();
		return `${firstLocation} e arredores`;
	}

	if ((listingFilters.value.regions?.length || 0) > 1) {
		return 'sua busca';
	}

	return (
		listingFilters.value.regions?.[0] ||
		listingFilters.value.cities?.[0] ||
		listingFilters.value.macroregions?.[0] ||
		listingFilters.value.states?.[0] ||
		infoLabelFallback.value
	);
});

const title = computed(() => {
	if (props.titleOverride) return props.titleOverride;

	return titleLocation.value
		? `Explore ${titleLocation.value} em vídeos`
		: 'Descubra imóveis em vídeo';
});

const { isShortsSrpVariant } = useShortsSrpVariant();

const shortsFilters = computed(() => {
	const baseFilters = {
		regions: listingFilters.value.regions,
		cities: listingFilters.value.cities,
		states: listingFilters.value.states,
		macroregions: listingFilters.value.macroregions,
		propertyTypes: listingFilters.value.propertyTypes,
		minAskingPrice: listingFilters.value.minAskingPrice,
		maxAskingPrice: listingFilters.value.maxAskingPrice,
		excludeProperties: listingFilters.value.excludeProperties,
		poiLocationLat: listingFilters.value.poiLocationLat,
		poiLocationLng: listingFilters.value.poiLocationLng
	};

	if (isShortsSrpVariant.value) {
		return withSrpVariantRecommendationParams({
			...baseFilters,
			minArea: listingFilters.value.minArea,
			maxArea: listingFilters.value.maxArea
		});
	}

	return {
		...baseFilters,
		sortBy: SHORTS_SORT_BY.SUCCESS_PROBABILITY,
		orderBy: ORDER_BY.DESC
	};
});

const shouldRenderShortsSection = computed(
	() => !props.deferUntilVisible || hasEnteredViewport.value
);
</script>

<template>
	<div
		ref="rootRef"
		class="-mx-5 w-[calc(100%+40px)] md:-mr-8 md:ml-0 md:w-[calc(100%+32px)] lg:mx-0 lg:w-full"
	>
		<SectionsShortsSection
			v-if="shouldRenderShortsSection"
			:title="title"
			:filters="shortsFilters"
			:fetch-page="props.fetchPage"
			:fetch-per-page="props.fetchPerPage"
			:max-videos="props.maxVideos"
			variant="light"
			single-card-on-lg
			use-compact-mobile-top-spacing
			hide-bottom-border
			:show-title-icon="false"
			slider-section-name="shorts"
			video-card-section-name="shorts"
			cta-section-name="shorts"
			@update:has-videos="handleShortsHasVideos"
		/>
		<SectionSkeleton
			v-else
			:title="title"
			variant="light"
			hide-bottom-border
			aria-hidden="true"
		/>
	</div>
</template>
